import type { ResolvedCollection } from '@rstore/shared';
import type { RstoreConnectOptions } from '~~/shared/types/rstore';
import type { Store } from '#imports';
import type { ResponseEnvelope } from '#layers/base/public';
import type { AnyItem, Filter, ResolvedMonospaceCollection } from '#layers/data-model/public';
import { klona } from 'klona';
import { useAppConfig } from '#app/config';
import { defineRstorePlugin } from '#imports';
import { CrudAction } from '#layers/access/public';
import { isGeneratedKey, isUuid } from '#layers/base/public';
import { buildFilterFromItemKeys, getCollectionFields, getCollectionPrimaryKeyFields, isEmptyFilter, isNonEmptyFilter, isPrimitiveTypeNumber, isPrimitiveTypeString, isPrimitiveTypeUuid, matchesFilter, mergeAnd, parseItemPrimaryKeyString, pruneIncompleteFilter, toCompositePrimaryKey } from '#layers/data-model/public';
import { applyRelationOperationsToCache } from '~/rstore/utils/apply-relation-operations-to-cache';
import { getFetchOptions } from '~/rstore/utils/get-fetch-options';
import { hasCompositeKey } from '~/rstore/utils/has-composite-key';
import { prepareRelationFields } from '~/rstore/utils/prepare-relation-fields';
import { sortItems } from '~/rstore/utils/sort-items';
import { trimPaginationSentinel } from '~/rstore/utils/trim-pagination-sentinel';
import { unwrapNestedDataEnvelopes } from '~/rstore/utils/unwrap-nested-data-envelopes';

type AnyItemResponseSingle = ResponseEnvelope<AnyItem>;
type AnyItemResponseMany = ResponseEnvelope<AnyItem[]>;

export default defineRstorePlugin({
	name: 'monospace',
	scopeId: 'monospace-default',
	category: 'remote',

	setup({ hook }) {
		const appConfig = useAppConfig();

		hook('fetchMany', async ({ store, collection, findOptions, getResult, setResult, meta }) => {
			const { endpoint, query, paginated } = getFetchOptions({
				appConfig,
				store,
				collection,
				findOptions,
				operation: CrudAction.Read,
			});

			const response = await $fetch<AnyItemResponseMany>(endpoint, { query });

			const responseParser = collection.meta?.parse?.many ?? ((value) => value);
			let result = responseParser(response.data) as any[];
			result = unwrapNestedDataEnvelopes(result, collection, store);

			const responseTotalCount = response.meta?.totalCount;

			if (typeof responseTotalCount === 'number' && Number.isFinite(responseTotalCount) && responseTotalCount >= 0) {
				meta.totalCount = responseTotalCount;
			}
			else if (paginated && result.length < findOptions!.pageSize! + 1) {
				meta.totalCount = findOptions!.pageIndex! * findOptions!.pageSize! + result.length;
			}
			else {
				meta.totalCount = undefined;
			}

			const items = trimPaginationSentinel(result, {
				paginated,
				pageSize: findOptions?.pageSize,
			});

			setResult([
				...(getResult() ?? []),
				...items,
			]);
		});

		hook('fetchFirst', async ({ store, collection, setResult, findOptions, key }) => {
			if (isGeneratedKey(key)) {
				return; // already fetched
			}

			const {
				endpoint,
				query,
			} = getFetchOptions({
				appConfig,
				store,
				collection,
				key,
				findOptions,
				operation: CrudAction.Read,
			});

			const { data: response } = await $fetch<AnyItemResponseSingle>(endpoint, { query });

			const responseParser = collection.meta?.parse?.many ?? ((value) => value);
			let result = responseParser(response);
			result = unwrapNestedDataEnvelopes(result, collection, store);

			setResult(Array.isArray(result) ? result[0] : result);
		});

		hook('createItem', async ({ store, collection, setResult, item }) => {
			if (isGeneratedKey(collection.getKey(item))) {
				return;
			}

			const { endpoint, query } = getFetchOptions({
				appConfig,
				store,
				collection,
				operation: CrudAction.Create,
			});

			item = prepareCreateItem(item, { collection, allCollections: store.$collections });

			const { data: result } = await $fetch<AnyItemResponseSingle>(endpoint, {
				method: 'POST',
				body: item,
				query,
			});

			let resultItem = Array.isArray(result) ? result[0] : result;

			if (!resultItem) {
				throw new Error('No item returned from createItem');
			}

			resultItem = unwrapNestedDataEnvelopes(resultItem, collection, store);

			if (collection.meta?.fetchOnCreate) {
				const key = collection.getKey(resultItem);

				if (!key) {
					throw new Error('No key found for created item');
				}

				setResult(await (store as Store).$collection(collection.name).findFirst({
					key,
					fetchPolicy: 'no-cache',
				}));
			}
			else {
				setResult(resultItem);
			}
		});

		hook('createMany', async ({ store, collection, setResult, items }) => {
			// filter out all generated items
			items = items.filter((item) => !isGeneratedKey(collection.getKey(item)));

			if (items.length === 0) {
				return;
			}

			const { endpoint, query } = getFetchOptions({
				appConfig,
				store,
				collection,
				operation: CrudAction.Create,
			});

			items = items.map((item) => prepareCreateItem(item, { collection, allCollections: store.$collections }));

			let { data: resultItems } = await $fetch<AnyItemResponseMany>(endpoint, {
				method: 'POST',
				body: items,
				query,
			});

			if (!resultItems) {
				throw new Error('No items returned from createItem');
			}

			resultItems = unwrapNestedDataEnvelopes(resultItems, collection, store);

			if (collection.meta?.fetchOnCreate) {
				const resolvedCollection = store.MonospaceCollection.peekFirst({ filter: { apiName: collection.name } });

				if (!resolvedCollection) {
					console.error(`Could not find collection ${collection.name} in schema store`);
					return;
				}

				const filter = buildFilterFromItemKeys({ keys: resultItems, primaryKeyFields: getCollectionPrimaryKeyFields(resolvedCollection) });

				setResult(await (store as Store).$collection(collection.name).findMany({
					filter,
					fetchPolicy: 'no-cache',
				}));
			}
			else {
				setResult(resultItems);
			}
		});

		hook('updateItem', async ({ store, collection, key, setResult, item }) => {
			if (isGeneratedKey(key)) {
				return;
			}

			const {
				endpoint,
				query,
				fields,
			} = getFetchOptions({
				appConfig,
				store,
				collection,
				key,
				operation: CrudAction.Update,
			});

			// TODO uncomment when it's working in the engine
			if (collection.meta?.connect) {
				item = transformItemWithConnect(item, collection.meta.connect, false);
			}

			// Drop constrained FK fields and serialize values nested inside relation
			// operations (e.g. a JSON `tags` field on an M2M target) before sending;
			// rstore only serializes the top-level item.
			item = prepareRelationFields(item, collection, store.$collections);

			if (collection.meta?.transformBeforeUpdate) {
				item = collection.meta.transformBeforeUpdate(item);
			}

			if (Object.keys(item).filter((key) => item[key] !== undefined).length === 0) {
				return;
			}

			const { data: result } = await $fetch<AnyItemResponseSingle>(endpoint, {
				method: 'PATCH',
				body: item,
				query,
			});

			let resultItem = Array.isArray(result) ? result[0] : result;

			if (!resultItem) {
				throw new Error('No item returned from updateItem');
			}

			resultItem = unwrapNestedDataEnvelopes(resultItem, collection, store);

			const resolvedCollection = store.MonospaceCollection.peekFirst({ filter: { apiName: collection.name } });

			if (resolvedCollection) {
				const parentPK = toCompositePrimaryKey(key, resolvedCollection);
				applyRelationOperationsToCache(store, item, collection, parentPK);
			}

			if (collection.meta?.fetchOnUpdate) {
				const key = collection.getKey(resultItem);

				if (!key) {
					throw new Error('No key found for updated item');
				}

				const updatedItem = await (store as Store).$collection(collection.name).findFirst({
					key,
					fetchPolicy: 'no-cache',
					params: {
						fields,
					},
				});

				setResult(updatedItem);
			}
			else {
				setResult(resultItem);
			}
		});

		hook('deleteItem', async ({ store, collection, key }) => {
			if (isGeneratedKey(key)) {
				return;
			}

			const { endpoint, query } = getFetchOptions({
				appConfig,
				store,
				collection,
				key,
				operation: CrudAction.Delete,
			});

			await $fetch(endpoint, {
				method: 'DELETE',
				query,
			});
		});

		hook('deleteMany', async ({ store, collection, keys, abort }) => {
			keys = keys.filter((key) => !isGeneratedKey(key));

			if (keys.length === 0) {
				abort();
				return;
			}

			const resolvedCollection = store.MonospaceCollection.peekFirst({ filter: { apiName: collection.name } });

			if (!resolvedCollection) {
				console.error(`Could not find collection ${collection.name} in schema store`);
				return;
			}

			const parsedKeys = keys.map((key) => toApiCompatibleKey(key, collection, resolvedCollection));
			const primaryKeyFields = getCollectionPrimaryKeyFields(resolvedCollection);

			const { endpoint, query } = getFetchOptions({
				appConfig,
				store,
				collection,
				operation: CrudAction.Delete,
				fields: primaryKeyFields.map((field) => field.apiName),
				filter: buildFilterFromItemKeys({ keys: parsedKeys, primaryKeyFields }),
			});

			await $fetch(endpoint, {
				method: 'DELETE',
				query,
			});

			abort();
		});

		hook('cacheFilterFirst', ({ findOptions, readItemsFromCache, getResult, setResult }) => {
			const rawFilter = findOptions?.filter;
			const filter = rawFilter ? pruneIncompleteFilter(rawFilter) : undefined;

			if (filter && typeof filter === 'object' && !getResult()) {
				const items = readItemsFromCache();

				const item = items.find((item) => matchesFilter(item, filter));
				setResult(item);
			}
		});

		hook('cacheFilterMany', ({ collection, findOptions, getResult, setResult }) => {
			const rawFilter = findOptions?.filter;
			const filter = rawFilter ? pruneIncompleteFilter(rawFilter) : undefined;
			const hasFilter = isNonEmptyFilter(filter);
			const hasSort = findOptions?.sort && findOptions.sort.length > 0;

			if (hasFilter || hasSort) {
				let items = getResult();

				if (hasFilter) {
					items = items.filter((item) => matchesFilter(item, filter));
				}

				if (hasSort) {
					items = sortItems(items, findOptions.sort, collection.meta?.primitiveFieldTypes);
				}

				setResult(items);
			}
		});

		// Text search
		hook('resolveFindOptions', ({ store, collection, findOptions, updateFindOptions, many }) => {
			if (findOptions?.textSearch && many) {
				const dCollection = store.MonospaceCollection.peekFirst({ filter: { apiName: collection.name } });

				if (!dCollection) {
					throw new Error(`Could not find MonospaceCollection for collection ${collection.name}`);
				}

				const fields = getCollectionFields(dCollection, {
					includeRelationFields: false,
				});

				const searchFilter: Filter = {
					_or: [],
				};

				const search = findOptions.textSearch;

				for (const field of fields) {
					if ('type' in field) {
						/* eslint-disable unicorn/no-lonely-if */
						if (isPrimitiveTypeString(field.type)) {
							searchFilter._or.push({
								[field.apiName]: {
									_icontains: search,
								},
							});
						}
						else if (isPrimitiveTypeUuid(field.type)) {
							if (isUuid(search)) {
								searchFilter._or.push({
									[field.apiName]: {
										_eq: search,
									},
								});
							}
						}
						else if (isPrimitiveTypeNumber(field.type)) {
							if (!Number.isNaN(Number(search))) {
								searchFilter._or.push({
									[field.apiName]: {
										_eq: Number(search),
									},
								});
							}
						}
					}
					/* eslint-enable unicorn/no-lonely-if */
				}

				if (!isEmptyFilter(searchFilter)) {
					let filter = klona(findOptions.filter) ?? {};
					filter = mergeAnd(filter, searchFilter._or.length === 1 ? searchFilter._or[0]! : searchFilter);

					updateFindOptions({
						filter,
					});
				}
			}
		});
	},
});

function transformItemWithConnect(
	item: AnyItem,
	connectOptions: RstoreConnectOptions,
	isCreate: boolean,
) {
	for (const prop in connectOptions) {
		if (!item[prop]) {
			continue;
		}

		const [relationName, relationField] = connectOptions[prop]!;

		const connectOp = {
			_connect: {
				key: {
					[relationField]: item[prop],
				},
			},
		};

		item = {
			...item,
			// Add the relation expected by the backend
			[relationName]: isCreate ? connectOp : [connectOp],
			// Remove the prop from the item
			[prop]: undefined,
		};
	}

	return item;
}

function toApiCompatibleKey(key: string | number, collection: ResolvedCollection<any, any, any>, monospaceCollection: ResolvedMonospaceCollection) {
	if (hasCompositeKey(collection)) {
		return parseItemPrimaryKeyString(key as string, monospaceCollection);
	}

	return key;
}

interface PrepareCreateItemOptions {
	collection: ResolvedCollection;
	allCollections: ResolvedCollection[];
}

function prepareCreateItem(item: AnyItem, options: PrepareCreateItemOptions) {
	const { collection, allCollections } = options;

	if (collection.meta?.connect) {
		item = transformItemWithConnect(item, collection.meta.connect, true);
	}

	// Drop constrained FK fields and serialize values nested inside relation operations
	// (e.g. a JSON `tags` field on an M2M target) before sending; rstore only serializes
	// the top-level item.
	item = prepareRelationFields(item, collection, allCollections);

	if (collection.meta?.transformBeforeCreate) {
		item = collection.meta.transformBeforeCreate(item);
	}

	return item;
}
